import React, {useState, useCallback, memo} from 'react';
import type {CSSProperties} from 'react';
import Link from 'next/link';
import parse from 'html-react-parser';
import Container from '@/components/atoms/Container';
import Eyebrow from '@/components/atoms/Eyebrow';
import ArrowButton from '@/components/atoms/ArrowButton';
import {cx} from '@/util/cx';
import styles from './HomeServicesV2.module.scss';

interface SubServiceProps {
    heading: string;
    redirect: string;
}

interface ServiceData {
    iconKey?: string;
    title: string;
    description: string;
    subServices: SubServiceProps[];
    ctaText?: string;
    ctaUrl?: string;
}

interface HomeServicesV2Props {
    eyebrow?: string;
    heading: string;
    subheading: string;
    contentData: ServiceData[];
}

// Matches up(md) in the stylesheet: the width at which the accordion becomes the board.
const BOARD_QUERY = '(min-width: 900px)';

const HomeServicesV2 = ({eyebrow, heading, subheading, contentData}: HomeServicesV2Props) => {
    const [activeIndex, setActiveIndex] = useState(0);

    // The board always shows one panel; the accordion can also close the open one.
    const handleSelect = useCallback((index: number) => {
        const isBoard = globalThis.matchMedia(BOARD_QUERY).matches;
        setActiveIndex((current) => (!isBoard && current === index ? -1 : index));
    }, []);

    return (
        <Container>
            {/* Section Header */}
            <div className={styles.headingSection}>
                {eyebrow && <Eyebrow text={eyebrow} />}
                <h2 className={cx(styles.heading, 'font-headers')}>{heading}</h2>
                <p
                    className={cx(styles.subheading, 'font-regular-text generative-transforming-subheading')}
                    dangerouslySetInnerHTML={{__html: subheading}}
                />
            </div>

            {/* One copy of every service, laid out by CSS: an accordion on mobile, and on desktop a
                three-panel board (tabs | detail | sub-services). Every panel's copy and links stay in
                the server HTML for crawlers; only the active one is shown. */}
            <div className={styles.services} style={{'--service-rows': contentData.length} as CSSProperties}>
                {contentData.map((item, index) => {
                    const isActive = activeIndex === index;
                    return (
                        <div key={index} className={styles.item}>
                            <div className={cx(styles.tab, isActive && styles.tabActive)} onClick={() => handleSelect(index)}>
                                <span className={cx(styles.tabNumber, 'font-headers')}>{String(index + 1).padStart(2, '0')}</span>
                                <span className={cx(styles.tabLabel, 'font-headers')}>{item.title}</span>
                            </div>
                            <div className={cx(styles.collapse, isActive && styles.collapseOpen)}>
                                <div className={styles.collapseInner}>
                                    <div className={styles.panel}>
                                        <div className={styles.detail}>
                                            <h3 className={cx(styles.detailTitle, 'font-headers')}>{item.title}</h3>
                                            <p className={cx(styles.description, 'font-regular-text')}>
                                                {item.description ? parse(item.description) : ''}
                                            </p>
                                            {item.ctaText && item.ctaUrl && (
                                                <div className={styles.ctaWrap}>
                                                    <ArrowButton variant="primary" href={item.ctaUrl} target="_blank">
                                                        {item.ctaText}
                                                    </ArrowButton>
                                                </div>
                                            )}
                                        </div>

                                        <div className={styles.subServices}>
                                            {item.subServices.map((sub, idx) =>
                                                sub?.redirect ? (
                                                    <Link href={sub.redirect} target="_blank" key={idx} className={styles.subService}>
                                                        <span className={styles.subServiceText}>{sub.heading}</span>
                                                    </Link>
                                                ) : (
                                                    <div key={idx} className={styles.subService}>
                                                        <span className={styles.subServiceText}>{sub.heading}</span>
                                                    </div>
                                                )
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </Container>
    );
};

export default memo(HomeServicesV2);
